// modelsService.js
// UI-facing contracts for model-suite data.

import { GET, POST, BASE_URL } from './httpClient';

export const getGolfModelContext = async () => ({
    activeEvent: {
        eventId: 'demo-travelers-2026',
        eventName: 'Travelers Championship',
        courseName: 'TPC River Highlands',
        courseKey: 'tpc_river_highlands',
        phaseLabel: 'Pre-Round Setup',
        completedRound: 1,
        updatedAt: '2026-06-12T10:48:00-04:00'
    },
    freshness: [
        { key: 'field', label: 'Field Synced', status: 'success', updatedAt: '2026-06-12T10:42:00-04:00' },
        { key: 'odds', label: 'Odds Synced', status: 'success', updatedAt: '2026-06-12T10:44:00-04:00' },
        { key: 'scores', label: 'Scores Synced', status: 'pending', updatedAt: '2026-06-12T09:55:00-04:00' },
        { key: 'params', label: 'Model Params Ready', status: 'success', updatedAt: '2026-06-12T10:47:00-04:00' }
    ],
    players: [
        { playerId: 'p1', playerName: 'Scottie Scheffler', rank: 1, initials: 'SS' },
        { playerId: 'p2', playerName: 'Rory McIlroy', rank: 2, initials: 'RM' },
        { playerId: 'p3', playerName: 'Xander Schauffele', rank: 3, initials: 'XS' },
        { playerId: 'p4', playerName: 'Collin Morikawa', rank: 4, initials: 'CM' },
        { playerId: 'p5', playerName: 'Ludvig Aberg', rank: 5, initials: 'LA' },
        { playerId: 'p6', playerName: 'Viktor Hovland', rank: 6, initials: 'VH' }
    ]
});

export const getGolfOutrightsPreview = async () => ([
    {
        playerId: 'p1',
        playerName: 'Scottie Scheffler',
        rank: 1,
        winProb: 18.4,
        cutProb: 99.2,
        top5Prob: 42.1,
        top10Prob: 58.7,
        top20Prob: 76.4,
        courseFit: 'A',
        avgProjectedScore: 68.9,
        skillProfile: 'Elite Tee-to-Green'
    },
    {
        playerId: 'p2',
        playerName: 'Rory McIlroy',
        rank: 2,
        winProb: 11.8,
        cutProb: 97.8,
        top5Prob: 31.4,
        top10Prob: 46.2,
        top20Prob: 64.5,
        courseFit: 'A-',
        avgProjectedScore: 69.4,
        skillProfile: 'Power Scoring'
    },
    {
        playerId: 'p3',
        playerName: 'Xander Schauffele',
        rank: 3,
        winProb: 9.7,
        cutProb: 96.9,
        top5Prob: 27.2,
        top10Prob: 41.8,
        top20Prob: 59.1,
        courseFit: 'B+',
        avgProjectedScore: 69.7,
        skillProfile: 'Balanced Profile'
    },
    {
        playerId: 'p4',
        playerName: 'Collin Morikawa',
        rank: 4,
        winProb: 7.6,
        cutProb: 95.4,
        top5Prob: 22.6,
        top10Prob: 36.3,
        top20Prob: 53.7,
        courseFit: 'B+',
        avgProjectedScore: 70.1,
        skillProfile: 'Approach Edge'
    }
]);

export const getGolfMatchupDiscovery = async () => {
    const response = await GET(`${BASE_URL}/model-predictions/golf/matchups/discovery`);
    const data = response.data || {};

    if (!data.success) {
        throw new Error(data.error || 'Unable to load matchup discovery.');
    }

    return data.results || {};
};

export const getGolfMatchupSource = async ({ eventId, round } = {}) => {
    const response = await GET(
        `${BASE_URL}/model-predictions/golf/events/${encodeURIComponent(eventId)}/matchup-source`,
        { round }
    );
    const data = response.data || {};

    if (!data.success) {
        throw new Error(data.error || 'Unable to load matchup source data.');
    }

    return data.results || {};
};

export const getLatestGolfLiveOutrights = async () => {
    const response = await GET(`${BASE_URL}/model-predictions/golf/outrights/live/latest`);
    const data = response.data || {};

    if (!data.success) {
        if (data.error === 'Live outright prediction not found') {
            return null;
        }

        throw new Error(data.error || 'Unable to load live outright board.');
    }

    return data.results || null;
};

const toDisplayPercent = (value) => {
    const numberValue = Number(value || 0);
    return Math.abs(numberValue) <= 1 ? numberValue * 100 : numberValue;
};

const normalizePredictionPlayer = (player = {}) => ({
    playerId: player.player_id || player.playerId || '',
    playerName: player.player_name || player.playerName || 'Player',
    mu: Number(player.mu || 0),
    sigma: Number(player.sigma || 0)
});

const normalizePredictionOutcome = (outcome = {}) => ({
    outcomeType: outcome.outcome_type || outcome.outcomeType || '',
    playerId: outcome.player_id || outcome.playerId || '',
    playerName: outcome.player_name || outcome.playerName || '',
    probability: toDisplayPercent(outcome.probability),
    fairOdds: outcome.fair_odds || outcome.fairOdds || '',
    components: outcome.components
        ? Object.entries(outcome.components).reduce((nextComponents, [key, value]) => ({
            ...nextComponents,
            [key]: toDisplayPercent(value)
        }), {})
        : null
});

export const normalizeGolfMatchupPrediction = (predictionDocument = {}) => {
    const prediction = predictionDocument.prediction || predictionDocument || {};
    const marketType = prediction.market_type || prediction.marketType || predictionDocument.market || '';
    const predictionVersion = (
        prediction.prediction_version ||
        prediction.predictionVersion ||
        predictionDocument.prediction_version ||
        predictionDocument.predictionVersion ||
        ''
    );

    return {
        sport: predictionDocument.sport || 'golf',
        predictionType: predictionDocument.prediction_type || predictionDocument.predictionType || 'round_matchup',
        market: predictionDocument.market || marketType,
        subjectKey: predictionDocument.subject_key || predictionDocument.subjectKey || '',
        eventId: predictionDocument.event_id || predictionDocument.eventId || '',
        eventName: predictionDocument.event_name || predictionDocument.eventName || '',
        modelVersion: predictionDocument.model_version || predictionDocument.modelVersion || '',
        sourceJobId: predictionDocument.source_job_id || predictionDocument.sourceJobId || '',
        createdAt: predictionDocument.created_at || predictionDocument.createdAt || '',
        updatedAt: predictionDocument.updated_at || predictionDocument.updatedAt || '',
        expiresAt: predictionDocument.expires_at || predictionDocument.expiresAt || '',
        marketType,
        predictionVersion,
        settlement: prediction.settlement || '',
        nSims: Number(prediction.n_sims || prediction.nSims || 0),
        discretizeScores: Boolean(prediction.discretize_scores ?? prediction.discretizeScores),
        scoreSimulation: prediction.score_simulation || prediction.scoreSimulation || '',
        round: Number(prediction.round || predictionDocument.round || 0),
        courseKey: prediction.course_key || prediction.courseKey || predictionDocument.course_key || predictionDocument.courseKey || '',
        playerProjections: Array.isArray(prediction.player_projections || prediction.playerProjections)
            ? (prediction.player_projections || prediction.playerProjections).map(normalizePredictionPlayer)
            : [],
        outcomes: Array.isArray(prediction.outcomes)
            ? prediction.outcomes.map(normalizePredictionOutcome)
            : []
    };
};

export const createGolfMatchupPrediction = async (request = {}) => {
    const response = await POST(`${BASE_URL}/model-predictions/golf/matchups`, request);
    return {
        httpStatus: response.status,
        data: response.data,
        results: response.data?.results || {}
    };
};

export const getModelPredictionJob = async (jobId) => {
    const response = await GET(`${BASE_URL}/model-predictions/jobs/${jobId}`);
    return response.data?.results || {};
};

export const getModelPredictionBySubjectKey = async (subjectKey) => {
    const response = await POST(`${BASE_URL}/model-predictions/by-key`, { subjectKey });
    return response.data?.results || {};
};
